export interface ScoringWeights {
  identity: number
  presence: number
  behavior: number
}

export interface ViolationPenalties {
  noFace: number
  multipleFaces: number
  identityMismatch: number
  tabSwitch: number
  windowBlur: number
  fullscreenExit: number
}

/** Optional per-assessment time limit (stored in proctoring_config JSON) */
export interface AssessmentTimeLimitConfig {
  enabled: boolean
  durationMs: number
  /** Fire first warning when this much time remains */
  firstWarningAtRemainingMs: number
  /** Repeat warnings every N ms; 0 = only the first warning */
  warningIntervalMs: number
  showCountdown: boolean
}

export const DEFAULT_TIME_LIMIT_CONFIG: AssessmentTimeLimitConfig = {
  enabled: false,
  durationMs: 45 * 60 * 1000,
  firstWarningAtRemainingMs: 10 * 60 * 1000,
  warningIntervalMs: 5 * 60 * 1000,
  showCountdown: true,
}

export interface ProctoringConfig {
  verificationIntervalMs: number
  noFaceWarningThresholdMs: number
  identitySimilarityThreshold: number
  maxConsecutiveIdentityFailures: number
  scoringWeights: ScoringWeights
  violationPenalties: ViolationPenalties
  passingScore: number
  externalAssessmentUrl: string
  completionWebhookUrl: string
  completionWebhookSecret: string
  completionPollIntervalMs: number
  timeLimit: AssessmentTimeLimitConfig
  /**
   * Require fullscreen and disallow tab/window switching. When false, fullscreen is
   * not entered or enforced, and tab switches / blur / fullscreen exits are ignored
   * (no warnings, no violations, no score impact).
   */
  browserLockdown: boolean
}

/** Violation types that only apply when `browserLockdown` is enabled */
export const BROWSER_LOCKDOWN_VIOLATION_TYPES = ['tab_switch', 'window_blur', 'fullscreen_exit'] as const

export const DEFAULT_ASSESSMENT_PROCTORING: Omit<
  ProctoringConfig,
  'externalAssessmentUrl' | 'completionWebhookUrl' | 'completionWebhookSecret'
> = {
  verificationIntervalMs: 2000,
  noFaceWarningThresholdMs: 2000,
  identitySimilarityThreshold: 40,
  maxConsecutiveIdentityFailures: 999,
  scoringWeights: {
    identity: 0.30,
    presence: 0.35,
    behavior: 0.35,
  },
  violationPenalties: {
    noFace: 2,
    multipleFaces: 5,
    identityMismatch: 2,
    tabSwitch: 3,
    windowBlur: 1,
    fullscreenExit: 3,
  },
  passingScore: 50,
  completionPollIntervalMs: 3000,
  timeLimit: { ...DEFAULT_TIME_LIMIT_CONFIG },
  browserLockdown: true,
}
